import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  FilterAlt as FilterAltIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Quiz as QuizIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import AuthModal from '../auth/AuthModal';
import QuizResults from './normalMode/QuizResults';  
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const QuizHistory = () => {
  const { user, isAuthenticated } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showQuizDetails, setShowQuizDetails] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [topicFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 20;

  // Check if user is registered
  const isRegisteredUser = isAuthenticated && user?.user_type === 'registered';

  // Fetch quiz history
  const fetchQuizHistory = useCallback(async (page = 0, reset = false) => {
    if (!isRegisteredUser) return;
    
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: page * itemsPerPage,
        ...(dateRange.start && { start_date: dateRange.start }),
        ...(dateRange.end && { end_date: dateRange.end }),
        ...(difficultyFilter !== 'all' && { difficulty: difficultyFilter }),
        ...(topicFilter !== 'all' && { topic: topicFilter })
      });

      const response = await axios.get(`${API_BASE}/api/quiz-history?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.data.success) {
        const newHistory = response.data.history;
        setHistory(prev => reset ? newHistory : [...prev, ...newHistory]);
        setHasMore(response.data.pagination.hasMore);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error('Error fetching quiz history:', err);
      setError('Failed to load quiz history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isRegisteredUser, dateRange, difficultyFilter, topicFilter, itemsPerPage]);

  // Load more history
  const loadMore = () => {
    if (!loading && hasMore) {
      fetchQuizHistory(currentPage + 1, false);
    }
  };

  // Refresh history
  const refreshHistory = () => {
    fetchQuizHistory(0, true);
  };

  // Delete quiz history entry
  const deleteQuizHistory = async (historyId) => {
    try {
      const response = await axios.delete(`${API_BASE}/api/quiz-history/${historyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.data.success) {
        setHistory(prev => prev.filter(item => item.id !== historyId));
      }
    } catch (err) {
      console.error('Error deleting quiz history:', err);
      setError('Failed to delete quiz history entry.');
    }
  };

  // View quiz details
  const viewQuizDetails = (quizItem) => {
    setSelectedQuiz(quizItem);
    setShowQuizDetails(true);
  };

  // Group history by date
  const groupedHistory = history.reduce((acc, item) => {
    const date = new Date(item.created_at).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  // Filter history by search term
  const filteredGroupedHistory = Object.entries(groupedHistory).reduce((acc, [date, items]) => {
    const filteredItems = items.filter(item => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.quiz_config?.topic_name?.toLowerCase().includes(searchLower) ||
        item.quiz_config?.difficulty?.level_name?.toLowerCase().includes(searchLower) ||
        item.total_score.toString().includes(searchLower)
      );
    });
    
    if (filteredItems.length > 0) {
      acc[date] = filteredItems;
    }
    
    return acc;
  }, {});

  // Load history on component mount and when filters change
  useEffect(() => {
    if (isRegisteredUser) {
      fetchQuizHistory(0, true);
    }
  }, [isRegisteredUser, dateRange, difficultyFilter, topicFilter]);

  // Show auth modal for non-registered users
  useEffect(() => {
    if (isAuthenticated && !isRegisteredUser) {
      setShowAuthModal(true);
    }
  }, [isAuthenticated, isRegisteredUser]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format score
  const formatScore = (score) => {
    if (score === null || score === undefined) return '0';
    if (score > 0) return `+${score.toLocaleString()}`;
    if (score < 0) return score.toLocaleString();
    return '0';
  };

  // Handle auth modal close
  const handleAuthModalClose = () => {
    setShowAuthModal(false);
  };

  // Handle auth success
  const handleAuthSuccess = (user) => {
    setShowAuthModal(false);
    // Refresh history if user becomes registered
    if (user.user_type === 'registered') {
      fetchQuizHistory(0, true);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#212121', py: 6 }}>
    
        <Container maxWidth="md" sx={{ paddingTop: '6rem', textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Please sign in to view your quiz history.
          </Alert>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => window.location.href = '/'}
            size="large"
          >
            Go Home
          </Button>
        </Container>
      </Box>
    );
  }

  if (!isRegisteredUser) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#212121', py: 6 }}> 
        <Container maxWidth="md" sx={{ paddingTop: '6rem', textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Quiz history is only available for registered users. Sign up with your email to save and view your quiz history!
          </Alert>
          <AuthModal
            open={showAuthModal}
            onClose={handleAuthModalClose}
            onSuccess={handleAuthSuccess}
          />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#212121', py: 6 }}> 
      <Container maxWidth="lg" sx={{ paddingTop: '6rem' }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <HistoryIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
            Quiz History
          </Typography>
          <Typography variant="h6" color="gray.400" sx={{ maxWidth: 600, mx: 'auto' }}>
            View your past quiz performances and track your progress over time.
          </Typography>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 4, bgcolor: '#2a2a2a', border: '1px solid #3a3a3a' }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Search quizzes"
                variant="outlined"
                sx={{ flexGrow: 1, minWidth: 250 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  style: { color: '#fff', borderColor: '#666' }
                }}
              />

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FilterAltIcon color="action" />
                <TextField
                  label="From Date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                  InputProps={{
                    style: { color: '#fff' }
                  }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                  InputProps={{
                    style: { color: '#fff' }
                  }}
                />

                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: '#fff' }}>Difficulty</InputLabel>
                  <Select
                    value={difficultyFilter}
                    label="Difficulty"
                    onChange={(e) => setDifficultyFilter(e.target.value)}
                    sx={{ color: '#fff' }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="Easy">Easy</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Hard">Hard</MenuItem>
                    <MenuItem value="Very Hard">Very Hard</MenuItem>
                    <MenuItem value="Extra Hard">Extra Hard</MenuItem>
                  </Select>
                </FormControl>

                <Tooltip title="Refresh history">
                  <IconButton onClick={refreshHistory} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && history.length === 0 && (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Loading your quiz history...
            </Typography>
          </Box>
        )}

        {/* No History */}
        {!loading && Object.keys(filteredGroupedHistory).length === 0 && history.length === 0 && (
          <Card sx={{ bgcolor: '#2a2a2a', border: '1px solid #3a3a3a', textAlign: 'center', py: 6 }}>
            <CardContent>
              <QuizIcon sx={{ fontSize: 80, color: 'grey.500', mb: 2 }} />
              <Typography variant="h5" color="grey.400" gutterBottom>
                No Quiz History Found
              </Typography>
              <Typography variant="body1" color="grey.500" sx={{ mb: 3 }}>
                You haven't completed any quizzes yet. Start playing to build your history!
              </Typography>
              <Button
                variant="contained"
                startIcon={<QuizIcon />}
                onClick={() => window.location.href = '/quiz'}
                size="large"
              >
                Start Quiz
              </Button>
            </CardContent>
          </Card>
        )}

        {/* History Accordion */}
        {Object.keys(filteredGroupedHistory).length > 0 && (
          <>
            {Object.entries(filteredGroupedHistory).map(([date, items]) => (
              <Accordion key={date} sx={{ bgcolor: '#1a1a1a', color: '#fff', mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography fontWeight="bold">
                    {date} ({items.length} {items.length === 1 ? 'quiz' : 'quizzes'})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {items.map((item) => (
                    <Card
                      key={item.id}
                      sx={{
                        mb: 2,
                        bgcolor: '#252525',
                        border: '1px solid #3a3a3a',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        }
                      }}
                      onClick={() => viewQuizDetails(item)}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box flex={1}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                              {item.quiz_config?.topic_name || 'General Quiz'}
                            </Typography>
                            <Box display="flex" gap={1} mb={1}>
                              <Chip
                                label={item.quiz_config?.difficulty?.level_name || 'Medium'}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                label={`${item.quiz_config?.duration_minutes || 0} min`}
                                size="small"
                                icon={<AccessTimeIcon />}
                                variant="outlined"
                              />
                            </Box>
                            <Typography variant="body2" color="grey.400">
                              Completed: {new Date(item.created_at).toLocaleString()}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={2}>
                            <Box textAlign="center">
                              <Typography variant="h4" color="primary.main" fontWeight="bold">
                                {formatScore(item.total_score)}
                              </Typography>
                              <Typography variant="caption" color="grey.400">
                                Total Score
                              </Typography>
                            </Box>
                            <Box textAlign="center">
                              <Typography variant="h6" color="success.main" fontWeight="bold">
                                {formatTime(item.completion_time)}
                              </Typography>
                              <Typography variant="caption" color="grey.400">
                                Duration
                              </Typography>
                            </Box>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteQuizHistory(item.id);
                              }}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <Box textAlign="center" mt={4}>
                <Button
                  variant="outlined"
                  onClick={loadMore}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Quiz Details Dialog */}
        <Dialog
          open={showQuizDetails}
          onClose={() => setShowQuizDetails(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#212121',
              color: 'white',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5">
                Quiz Details - {selectedQuiz?.quiz_config?.topic_name || 'General Quiz'}
              </Typography>
              <IconButton onClick={() => setShowQuizDetails(false)} color="inherit">
                <CancelIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedQuiz && (
              <QuizResults
                config={selectedQuiz.quiz_config}
                quizData={selectedQuiz.quiz_data}
                results={selectedQuiz.results}
                onReplay={() => setShowQuizDetails(false)}
                onGoHome={() => setShowQuizDetails(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default QuizHistory;

